'use client';

import {cn} from '@/matic/lib/utils';

type ToggleProps = {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label?: string;
  disabled?: boolean;
  size?: 'sm' | 'md';
};

/**
 * Pill-style toggle switch matching the MATIC design system.
 *
 * @example
 * <Toggle checked={enabled} onChange={setEnabled} label="Custom" />
 */
export function Toggle({
  checked,
  onChange,
  label,
  disabled = false,
  size = 'md',
}: ToggleProps) {
  const trackClass =
    size === 'sm'
      ? 'h-4 w-7 focus:ring-offset-1'
      : 'h-5 w-9 focus:ring-offset-2';
  const thumbClass = size === 'sm' ? 'h-3 w-3' : 'h-4 w-4';
  const translateClass = size === 'sm' ? 'translate-x-3' : 'translate-x-4';

  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => {
        onChange(!checked);
      }}
      disabled={disabled}
      className={cn(
        'relative inline-flex flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500',
        trackClass,
        checked ? 'bg-primary-500' : 'bg-gray-200',
        disabled && 'cursor-not-allowed opacity-50'
      )}
    >
      <span className="sr-only">{label ?? 'Toggle'}</span>
      <span
        className={cn(
          'pointer-events-none inline-block transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out',
          thumbClass,
          checked ? translateClass : 'translate-x-0'
        )}
      />
    </button>
  );
}
